'use client';

import { Zap } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Tip } from '@/components/ui/tooltip';
import {
  toggleWorkspacePanel,
  useWorkspacePanelOpen,
} from './panels/panel-events';
import { cn } from '@/lib/utils';

/**
 * Bottom-right floating Activity button. Opens the bottom Marketplace
 * panel and asks it to select the Activity tab. Intentionally has no
 * unread badge — notifications live on the dedicated bell FAB in the
 * top-right; doubling them up here was misleading because this FAB
 * surfaces marketplace activity, not the user's notification feed.
 */
export function ActivityFab({ className }: { className?: string }) {
  const t = useT();
  // Track the bottom panel's open state so the FAB renders a "pressed"
  // sunken state while the activity drawer is visible (instead of
  // swapping the glyph). The bolt stays put as the FAB's identity —
  // the depressed surface + inset shadow tell the user it's active.
  const panelOpen = useWorkspacePanelOpen('bottom');

  return (
    <Tip label={panelOpen ? t('fab.closeActivity') : t('workspace.canvas.zap.activity')}>
      <button
        type="button"
        onClick={() =>
          toggleWorkspacePanel('bottom', { tab: 'marketplace:activity' })
        }
        aria-label={panelOpen ? t('fab.closeActivity') : t('fab.openActivity')}
        aria-expanded={panelOpen}
        aria-pressed={panelOpen}
        // Onboarding overlay anchor — the sketch `marketplace` caption
        // pins to the bolt FAB. Mounting the attribute on the button
        // (not the wrapping div) keeps the rect glued to the visible
        // chrome so the arrow lands on something the user can see.
        data-onboarding-anchor="activity"
        className={cn(
          // Harmonized with BrandCard / Notifications / Create FABs:
          // same plain `liquid-glass` surface (the pill-strength
          // displacement), same `ws-fab-pressed` overlay on open. The
          // bottom FABs share Create's geometry (h-14, rounded-full)
          // but use the same chrome recipe as the smaller top FABs so
          // the whole canvas reads as one family of glass pucks.
          'pointer-events-auto relative inline-flex h-14 w-14 items-center justify-center rounded-full select-none transition-all liquid-glass',
          !panelOpen && 'active:scale-95 hover:shadow-[0_6px_24px_rgba(0,0,0,0.14)]',
          // Single source of truth for "the open one is this" —
          // `.liquid-glass.ws-fab-pressed` (globals.css) swaps to the
          // active displacement filter so the bezel still refracts
          // when the panel is open, just with a different curve.
          panelOpen && 'ws-fab-pressed',
          className,
        )}
      >
        <Zap
          className={cn(
            'h-6 w-6 transition-colors',
            panelOpen ? 'text-foreground' : 'text-foreground/80',
          )}
          strokeWidth={1.5}
        />
      </button>
    </Tip>
  );
}
